import { createSelector } from "@reduxjs/toolkit";

import { RootState } from "./store";
import { ChannelSelector, EventType } from "../slices/local/orchestrator";
import { Cube } from "../players";
import { Cube as CubeType } from "../slices/local/user";

const selectUserPlayers = (state: RootState) => state?.user?.players;

const selectUserStations = (state: RootState) => state?.user?.stations;

const selectUserJingles = (state: RootState) => state?.user?.jingles;

const selectOrchestrator = (state: RootState) => state?.orchestrator;

const selectFilter = (state: RootState) => state?.filter;



const selectSort = (state: RootState) => state?.sort;



export const selectConnection = (state: RootState) => state?.connection;

export const selectPlayers = createSelector([selectUserPlayers], (players) => {
  return players;
}, {
  devModeChecks: { identityFunctionCheck: 'never' }
});

export const selectCubes = createSelector([selectPlayers], (players) => {
  return Object.values(players).filter((p): p is CubeType => p.type === Cube);
});

export const selectFilteredCubes = createSelector(
  [selectCubes, selectFilter],
  (cubes, filter) => {
    const cubeFilter = filter?.cubeFilter;
    if (!cubeFilter || (!cubeFilter.text && !cubeFilter.type)) return cubes;

    return cubes.filter(cube => {
      const matchesText = cubeFilter.text
        ? (cube.name || '').toLowerCase().includes(cubeFilter.text.toLowerCase()) ||
          (cube.serial || '').toLowerCase().includes(cubeFilter.text.toLowerCase())
        : true;

      let matchesType = true;
      if (cubeFilter.type === 'virtual') {
        matchesType = (cube.serial || '').toUpperCase().startsWith('TCV');
      } else if (cubeFilter.type === 'hardware') {
        matchesType = !(cube.serial || '').toUpperCase().startsWith('TCV');
      }

      return matchesText && matchesType;
    });
  }
);

export const selectSortedFilteredCubes = createSelector(
  [selectFilteredCubes, selectSort],
  (cubes, sort) => {
    const sortKey = sort?.cubeSort;
    if (!sortKey) return cubes;
    const sorted = [...cubes];
    switch (sortKey) {
      case 'name_asc':
        return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      case 'name_desc':
        return sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
      case 'serial_asc':
        return sorted.sort((a, b) => (a.serial || '').localeCompare(b.serial || ''));
      case 'serial_desc':
        return sorted.sort((a, b) => (b.serial || '').localeCompare(a.serial || ''));
      default:
        return sorted;
    }
  }
);

export enum StationType {
  Tailormade = 'tailormade',
  Branding = 'branding',
  Event = 'event',
  Standard = 'standard',
  DJ = 'dj',
  Jingle = 'jingle'
}

export const selectStations = createSelector([selectUserStations], (stations) => {
  const stationType = [
    StationType.Tailormade,
    StationType.Branding,
    StationType.Event,
    StationType.Standard,
    StationType.DJ,
    StationType.Jingle
  ];

  const toStationTypeEnum = (type: string | undefined): StationType => {
    switch (type) {
      case StationType.Branding:
        return StationType.Branding;
      case StationType.Tailormade:
        return StationType.Tailormade;
      case StationType.Event:
        return StationType.Event;
      case StationType.DJ:
        return StationType.DJ;
      default:
        return StationType.Standard;
    }
  };

  return stations.slice().sort((a, b) => {
    const typeA = stationType.indexOf(toStationTypeEnum(a.productStationType));
    const typeB = stationType.indexOf(toStationTypeEnum(b.productStationType));
    if (typeA < typeB) {
      return -1;
    }
    if (typeA > typeB) {
      return 1;
    }
    // If stationType is the same, compare by trimmed displayName (case insensitive)
    const nameA = a.displayName.trim().toUpperCase();
    const nameB = b.displayName.trim().toUpperCase();
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  });
});

export const selectFilteredStations = createSelector(
  [selectStations, selectFilter],
  (stations, filter) => {
    const stationFilter = filter?.stationFilter;
    if (!stationFilter || (!stationFilter.text && !stationFilter.type)) return stations;

    return stations.filter(station => {
      const matchesText = stationFilter.text
        ? station.displayName.toLowerCase().includes(stationFilter.text.toLowerCase())
        : true;
      let matchesType = true;
      if (stationFilter.type) {
        if (stationFilter.type === StationType.Branding) {
          matchesType =
            station.productStationType === StationType.Branding ||
            station.productStationType === StationType.Tailormade;
        } else {
          if (
            (station.productStationType == null || station.productStationType === '') &&
            station.stationType === 'catering' &&
            stationFilter.type === StationType.Standard
          ) {
            matchesType = true;
          } else {
            matchesType = station.productStationType === stationFilter.type;
          }
        }
      }
      return matchesText && matchesType;
    });
  }
);

export const selectJingles = createSelector([selectUserJingles], (jingles) => {
  return jingles;
}, {
  devModeChecks: { identityFunctionCheck: 'never' }
});

export const selectFilteredJingles = createSelector(
  [selectJingles, selectFilter],
  (jingles, filter) => {
    const stationFilter = filter?.stationFilter;
    if (!stationFilter || (!stationFilter.text && !stationFilter.type)) return jingles;

    return jingles.filter(jingle => {
      if (stationFilter.type && stationFilter.type !== StationType.Jingle) {
        return false;
      }
      const matchesText = stationFilter.text
        ? jingle.name.toLowerCase().includes(stationFilter.text.toLowerCase())
        : true;
      return matchesText;
    });
  }
);

export const selectChannelState = createSelector(
  [
    selectOrchestrator,
    // Passthrough channel selector to have both in output selector
    (state, channelSelector: ChannelSelector) => channelSelector
  ],
  (orchestratorState, { playerId, channel }) =>
    orchestratorState?.players[playerId]?.channels[channel]
);

export const selectRatedSongs = createSelector(
  [
    selectOrchestrator
  ],
  orchestratorState =>
    Object.values(orchestratorState.songEvents)
      .flatMap(event => event)
      .filter(event => event.type === EventType.Rated)
      .map(event => event.song)
);
